import React from "react";
import { Mafs, Line, Text, Theme, Coordinates, Plot } from "mafs";

// degrees -> radians
const toRad = (deg) => (deg * Math.PI) / 180;

// normalize radians to [0, 2π)
const normRad = (r) => {
    const twoPi = 2 * Math.PI;
    return ((r % twoPi) + twoPi) % twoPi;
};

// compute minor arc between start and end rays
const minorArc = (startRad, endRad) => {
    const twoPi = 2 * Math.PI;
    const s = normRad(startRad);
    const e = normRad(endRad);

    const ccw = (e - s + twoPi) % twoPi;
    const cw = (s - e + twoPi) % twoPi;

    return ccw <= cw ? { direction: +1, sweep: ccw } : { direction: -1, sweep: cw };
};

export const AngleGeometry = ({ config = {} }) => {
    const lines = Array.isArray(config.lines) ? config.lines : [];
    const angles = Array.isArray(config.angles) ? config.angles : [];

    // If no explicit lines are provided, draw the two rays for each angle so the arc makes sense.
    const drawRays = lines.length === 0;

    // Map parallelGroupId -> markerCount (1 for first group, 2 for second, then cap at 2)
    const groupCount = new Map();
    let nextGroupIdx = 1;
    const markerCountFor = (groupId) => {
        const key = groupId || "__default__";
        if (!groupCount.has(key)) {
            groupCount.set(key, Math.min(nextGroupIdx, 2));
            nextGroupIdx += 1;
        }
        return groupCount.get(key);
    };

    return (
        <div className="ch-widget-wrapper border rounded-xl bg-white overflow-hidden">
            <Mafs viewBox={{ x: [-5, 5], y: [-4, 4] }} pan={false} height={400}>
                <Coordinates.Cartesian visible={false} />

                {/* Render Lines */}
                {lines.map((l, i) => {
                    const key = l.id ?? `line-${i}`;
                    const showParallel = Boolean(l.isParallel);
                    const count = showParallel ? markerCountFor(l.parallelGroupId || l.id) : 0;

                    return (
                        <React.Fragment key={key}>
                            <Line.Segment point1={[l.x1, l.y1]} point2={[l.x2, l.y2]} weight={2} />
                            {showParallel && <ParallelMarker line={l} count={count} />}
                        </React.Fragment>
                    );
                })}

                {/* Render Angle Arcs & Labels */}
                {angles.map((a, i) => {
                    const key = a.id ?? `angle-${i}`;
                    const vx = Number.isFinite(a.vertexX) ? a.vertexX : 0;
                    const vy = Number.isFinite(a.vertexY) ? a.vertexY : 0;

                    const start = toRad(Number.isFinite(a.startAngle) ? a.startAngle : 0);
                    const end = toRad(Number.isFinite(a.endAngle) ? a.endAngle : 90);

                    // Respect backend flag
                    const showArc = a.showArc !== false;

                    // Use the minor arc for typical school diagrams (acute/obtuse interior angle)
                    const { direction, sweep } = minorArc(start, end);

                    const radius = 0.6; // aligned with Java config (no per-angle radius field)
                    const color = a.color ?? Theme.blue;

                    // ray endpoints (for optional ray drawing + label positioning)
                    const fromPoint = [vx + Math.cos(start), vy + Math.sin(start)];
                    const toPoint = [vx + Math.cos(end), vy + Math.sin(end)];

                    // label near arc midpoint (aligned with arc direction)
                    const mid = normRad(start + direction * (sweep / 2));
                    const labelX = vx + (radius + 0.2) * Math.cos(mid);
                    const labelY = vy + (radius + 0.2) * Math.sin(mid);

                    return (
                        <React.Fragment key={key}>
                            {/* Only draw rays when no explicit lines provided */}
                            {drawRays && (
                                <>
                                    <Line.Segment point1={[vx, vy]} point2={fromPoint} weight={2} />
                                    <Line.Segment point1={[vx, vy]} point2={toPoint} weight={2} />
                                </>
                            )}

                            {/* Arc */}
                            {showArc && sweep > 1e-6 && (
                                <Plot.Parametric
                                    domain={[0, sweep]}
                                    xy={(t) => {
                                        const theta = start + direction * t;
                                        return [vx + radius * Math.cos(theta), vy + radius * Math.sin(theta)];
                                    }}
                                    color={color}
                                    weight={2}
                                />
                            )}

                            {/* Label */}
                            {a.label ? (
                                <Text x={labelX} y={labelY} size={16}>
                                    {a.label}
                                </Text>
                            ) : null}
                        </React.Fragment>
                    );
                })}
            </Mafs>
        </div>
    );
};

// Parallel chevrons computed in world coords (no raw <g transform>)
const ParallelMarker = ({ line, count = 1 }) => {
    const x1 = line.x1,
        y1 = line.y1,
        x2 = line.x2,
        y2 = line.y2;

    const midX = (x1 + x2) / 2;
    const midY = (y1 + y2) / 2;

    const dx = x2 - x1;
    const dy = y2 - y1;
    const ang = Math.atan2(dy, dx);

    const ux = Math.cos(ang);
    const uy = Math.sin(ang);

    const chevronSize = 0.2;
    const spacing = 0.35;

    const drawChevronAt = (cx, cy, idx) => {
        const cos = Math.cos(ang);
        const sin = Math.sin(ang);

        const rot = ([x, y]) => [x * cos - y * sin, x * sin + y * cos];
        const add = ([x, y]) => [x + cx, y + cy];

        const pA = add(rot([-chevronSize, chevronSize]));
        const pB = add(rot([0, 0]));
        const pC = add(rot([-chevronSize, -chevronSize]));

        return (
            <React.Fragment key={`chev-${idx}`}>
                <Line.Segment point1={pA} point2={pB} weight={2} color={Theme.foreground} />
                <Line.Segment point1={pC} point2={pB} weight={2} color={Theme.foreground} />
            </React.Fragment>
        );
    };

    const centers =
        count >= 2
            ? [
                [midX - spacing * ux, midY - spacing * uy],
                [midX + spacing * ux, midY + spacing * uy],
            ]
            : [[midX, midY]];

    return <>{centers.map(([cx, cy], idx) => drawChevronAt(cx, cy, idx))}</>;
};
